import { AUTHENTICATED } from '@mkcore/platform/schema';
import { CsvEnum, Id, IsoDate, IsoDateTime, ListQuery, Paginated, QueryBool, QueryVersion, defineAction, defineEvent, updateFields, z } from '@mkcore/shared';
import { engagementPermissions as P } from './permissions';

// Namespace engagement (docs/01-architectuur/05-api-en-mcp.md): the commercial agreement with a client (CO-0168),
// rates with an effective date (CO-0169, CO-0170), budgets (CO-0271) and the work settings and goals of a person
// (CO-0272, CO-0274).

export const BILLING_MODELS = ['fixed', 'tm', 'retainer', 'research'] as const;
export const ENGAGEMENT_STATUSES = ['active', 'on_hold', 'closed'] as const;
export const RETAINER_PERIODS = ['month', 'quarter', 'year'] as const;
/** Where a rate hangs; the most specific match wins (docs/01-architectuur/04-datamodel.md, Tarieven met ingangsdatum). */
export const RATE_LEVELS = ['organisation', 'engagement', 'project', 'work_type', 'person'] as const;
export const BillingModel = z.enum(BILLING_MODELS);
export const EngagementStatus = z.enum(ENGAGEMENT_STATUSES);
export const RetainerPeriod = z.enum(RETAINER_PERIODS);
export const RateLevel = z.enum(RATE_LEVELS);
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');
const Cents = z.number().int().min(0).max(1_000_000_000);
const Minutes = z.number().int().min(0).max(100_000_000);

export const Engagement = z
  .object({
    id: Id,
    organisationId: Id,
    organisationName: z.string(),
    name: z.string(),
    billingModel: BillingModel.describe('fixed: a fixed amount; tm: time and material; retainer: a periodic allowance; research: internal, not invoiced'),
    status: EngagementStatus,
    startsOn: IsoDate.nullable(),
    endsOn: IsoDate.nullable(),
    currency: z.string(),
    fixedAmountCents: z.number().int().nullable().describe('fixed: the agreed amount'),
    retainerPeriod: RetainerPeriod.nullable(),
    retainerMinutes: z.number().int().nullable().describe('retainer: the allowance per period, in minutes'),
    overageSellCents: z.number().int().nullable().describe('retainer: hourly rate above the allowance, in cents; null means the normal rate'),
    retainerRollover: z.boolean().describe('retainer: unused allowance of a period carries over to the next period only (CO-0273)'),
    purchaseOrder: z.string().nullable().describe('Purchase order number of the client; new invoices of this engagement take it (CO-1333)'),
    customerReference: z.string().nullable().describe('Reference of the client; new invoices of this engagement take it (CO-1333)'),
    budgetMinutes: z.number().int().nullable().describe('Budget in hours, as minutes; consumption comes from report.budget.status'),
    budgetCents: z.number().int().nullable().describe('Budget in money, in cents'),
    targetMarginBp: z.number().int().nullable().describe('The margin aimed for in basis points, 2500 = 25% (CO-0312); null: no target'),
    billableDefault: z.boolean().describe('Whether new time entries under this engagement are billable by default (CO-0171)'),
    agentTimeBillable: z.boolean().describe('Whether agent time on this engagement may be invoiced (CO-1269, ADR-0014); off by default'),
    slaClassId: Id.nullable().describe('The SLA class of its tickets (CO-0179); null: the default class'),
    supportProjectId: Id.nullable().describe('Where the hours of its tickets go by default'),
    supportMinutes: z.number().int().nullable().describe('Support hours per retainer period, in minutes'),
    notes: z.string(),
    leadId: Id.nullable().describe('The lead this engagement came from (CO-0582, CO-0594)'),
    archivedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Engagement');

const engagementFields = {
  name: z.string().trim().min(1).max(200),
  billingModel: BillingModel,
  status: EngagementStatus.default('active'),
  startsOn: IsoDate.nullable().optional(),
  endsOn: IsoDate.nullable().optional(),
  currency: z.string().trim().length(3).toUpperCase().optional(),
  fixedAmountCents: Cents.nullable().optional(),
  retainerPeriod: RetainerPeriod.nullable().optional(),
  retainerMinutes: Minutes.nullable().optional(),
  overageSellCents: Cents.nullable().optional(),
  retainerRollover: z.boolean().optional(),
  purchaseOrder: z.string().trim().max(100).nullable().optional(),
  customerReference: z.string().trim().max(200).nullable().optional(),
  budgetMinutes: Minutes.nullable().optional(),
  budgetCents: Cents.nullable().optional(),
  targetMarginBp: z.number().int().min(-10_000).max(10_000).nullable().optional().describe('The margin aimed for in basis points, 2500 = 25% (CO-0312)'),
  billableDefault: z.boolean().optional(),
  agentTimeBillable: z.boolean().optional(),
  slaClassId: Id.nullable().optional().describe('An SLA class of engagement.slaClass.list (CO-0179)'),
  supportProjectId: Id.nullable().optional().describe('A project of this engagement for the hours of its tickets'),
  supportMinutes: Minutes.nullable().optional().describe('Support hours per retainer period, in minutes'),
  notes: z.string().max(20_000).optional(),
};

export const listEngagements = defineAction({
  name: 'engagement.engagement.list',
  method: 'get',
  path: '/engagement/engagements',
  permission: P.keys.read,
  input: ListQuery.extend({
    organisationId: Id.optional(),
    q: z.string().max(200).optional(),
    status: CsvEnum(ENGAGEMENT_STATUSES).optional(),
    billingModel: CsvEnum(BILLING_MODELS).optional(),
    includeArchived: QueryBool.optional(),
  }),
  output: Paginated(Engagement),
  summary: 'List engagements, of one organisation or all',
});

export const getEngagement = defineAction({
  name: 'engagement.engagement.get',
  method: 'get',
  path: '/engagement/engagements/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Engagement,
  etag: true,
});

export const createEngagement = defineAction({
  name: 'engagement.engagement.create',
  method: 'post',
  path: '/engagement/engagements',
  permission: P.keys.create,
  input: z.object({ organisationId: Id, ...engagementFields, leadId: Id.optional().describe('The lead it comes from; the lead gets this engagement (CO-0582)') }).strict(),
  output: Engagement,
  summary: 'Add an engagement under an organisation, with its billing model',
  description:
    'A retainer needs retainerPeriod and retainerMinutes; fixed needs fixedAmountCents. Fails with organisation-archived on an archived organisation. ' +
    'With leadId the lead (of the same organisation) is linked to the engagement.',
});

export const updateEngagement = defineAction({
  name: 'engagement.engagement.update',
  method: 'patch',
  path: '/engagement/engagements/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      ...(updateFields(engagementFields) as {
        [K in keyof typeof engagementFields]: z.ZodOptional<(typeof engagementFields)[K]>;
      }),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Engagement,
  etag: true,
});

export const linkLeadToEngagement = defineAction({
  name: 'engagement.engagement.linkLead',
  method: 'post',
  path: '/engagement/engagements/{id}/lead',
  permission: P.keys.update,
  input: z.object({ id: Id, leadId: Id.describe('A lead of the organisation of the engagement') }).strict(),
  output: Engagement,
  successStatus: 200,
  summary: 'Link a lead to this existing engagement instead of making a new one (#120)',
  description: 'Fails with lead-organisation-mismatch when the lead has another organisation or none yet (convert it first), and with engagement-archived.',
});

export const archiveEngagement = defineAction({
  name: 'engagement.engagement.archive',
  method: 'post',
  path: '/engagement/engagements/{id}/archive',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Engagement,
  successStatus: 200,
});

export const restoreEngagement = defineAction({
  name: 'engagement.engagement.restore',
  method: 'post',
  path: '/engagement/engagements/{id}/restore',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: Engagement,
  successStatus: 200,
});

// ---------- rates (CO-0169, CO-0170) ----------

export const Rate = z
  .object({
    id: Id,
    level: RateLevel,
    organisationId: Id.nullable(),
    engagementId: Id.nullable(),
    projectId: Id.nullable(),
    workTypeId: Id.nullable().describe('With engagementId or projectId: the rate of that work type within it'),
    principalId: Id.nullable().describe('person: the cost rate of a user or agent identity'),
    validFrom: IsoDate,
    sellCents: z.number().int().nullable().describe('Sell rate per hour in cents; null on a person rate'),
    costCents: z.number().int().nullable().describe('Cost rate per hour in cents, for margin'),
    currency: z.string(),
    label: z.string().describe('Human-readable: where the rate hangs'),
    createdAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Rate');

const rateTarget = {
  level: RateLevel,
  organisationId: Id.optional(),
  engagementId: Id.optional(),
  projectId: Id.optional(),
  workTypeId: Id.optional(),
  principalId: Id.optional(),
};

export const listRates = defineAction({
  name: 'engagement.rate.list',
  method: 'get',
  path: '/engagement/rates',
  permission: P.keys.read,
  input: ListQuery.extend({
    level: CsvEnum(RATE_LEVELS).optional(),
    organisationId: Id.optional(),
    engagementId: Id.optional(),
    projectId: Id.optional(),
    workTypeId: Id.optional(),
    principalId: Id.optional(),
  }),
  output: Paginated(Rate),
  summary: 'List rates, newest valid_from first; filter by where they hang',
});

export const createRate = defineAction({
  name: 'engagement.rate.create',
  method: 'post',
  path: '/engagement/rates',
  permission: P.keys.create,
  input: z
    .object({
      ...rateTarget,
      validFrom: IsoDate,
      sellCents: Cents.nullable().optional(),
      costCents: Cents.nullable().optional(),
      currency: z.string().trim().length(3).toUpperCase().optional(),
    })
    .strict(),
  output: Rate,
  summary: 'Add a rate with an effective date at a level of the hierarchy',
  description:
    'level organisation needs organisationId; engagement needs engagementId; project needs projectId; work_type needs workTypeId ' +
    '(optionally with engagementId or projectId to scope it); person needs principalId and a costCents. One rate per target and validFrom.',
});

export const updateRate = defineAction({
  name: 'engagement.rate.update',
  method: 'patch',
  path: '/engagement/rates/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      validFrom: IsoDate.optional(),
      sellCents: Cents.nullable().optional(),
      costCents: Cents.nullable().optional(),
      currency: z.string().trim().length(3).toUpperCase().optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Rate,
  etag: true,
  summary: 'Change a rate; existing time entries keep their stored rate until time.entry.recalculateRates',
});

export const deleteRate = defineAction({
  name: 'engagement.rate.delete',
  method: 'delete',
  path: '/engagement/rates/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ ok: z.literal(true) }),
  etag: true,
  kind: 'destructive',
});

export const ResolvedRate = z
  .object({
    sellCents: z.number().int().describe('0 when no rate applies'),
    costCents: z.number().int().describe('0 when no cost rate applies'),
    currency: z.string(),
    sellRateId: Id.nullable(),
    costRateId: Id.nullable(),
    sellLevel: RateLevel.nullable().describe('Which level supplied the sell rate'),
  })
  .openapi('ResolvedRate');

export const resolveRate = defineAction({
  name: 'engagement.rate.resolve',
  method: 'get',
  path: '/engagement/rates-resolve',
  permission: P.keys.read,
  input: z.object({
    date: IsoDate,
    organisationId: Id.optional(),
    engagementId: Id.optional(),
    projectId: Id.optional(),
    workTypeId: Id.optional(),
    principalId: Id.optional().describe('For the cost rate of the person'),
  }),
  output: ResolvedRate,
  summary: 'The effective sell and cost rate for a date: the most specific level with the latest valid_from on or before it',
});

// ---------- work settings and goals (CO-0272, CO-0274) ----------

export const WorkSettings = z
  .object({
    principalId: Id,
    workdays: z.array(z.number().int().min(1).max(7)).describe('ISO weekdays that are workdays, 1 = Monday'),
    minutesPerDay: z.number().int().describe('Expected minutes per workday'),
    weeklyGoalMinutes: z.number().int().nullable().describe('Goal in minutes per week; null means workdays × minutesPerDay'),
    monthlyGoalMinutes: z.number().int().nullable(),
    yearlyGoalMinutes: z.number().int().describe('The hours criterion of the Dutch tax office is 1225 hours'),
    billableGoalPercent: z.number().int().min(0).max(100).nullable().describe('Target share of billable time'),
    updatedAt: IsoDateTime,
  })
  .openapi('WorkSettings');

export const getWorkSettings = defineAction({
  name: 'engagement.workSettings.get',
  method: 'get',
  path: '/engagement/work-settings',
  permission: AUTHENTICATED,
  input: z.object({ principalId: Id.optional().describe('Another person needs engagements:read') }),
  output: WorkSettings,
  summary: 'Workdays, expected hours and goals of a person (defaults when never set)',
});

export const updateWorkSettings = defineAction({
  name: 'engagement.workSettings.update',
  method: 'put',
  path: '/engagement/work-settings',
  permission: AUTHENTICATED,
  input: z
    .object({
      principalId: Id.optional().describe('Another person needs engagements:update'),
      workdays: z.array(z.number().int().min(1).max(7)).min(0).max(7).optional(),
      minutesPerDay: Minutes.max(1440).optional(),
      weeklyGoalMinutes: Minutes.nullable().optional(),
      monthlyGoalMinutes: Minutes.nullable().optional(),
      yearlyGoalMinutes: Minutes.optional(),
      billableGoalPercent: z.number().int().min(0).max(100).nullable().optional(),
    })
    .strict(),
  output: WorkSettings,
  summary: 'Set workdays, expected hours per day and the goals per week, month and year',
});

// ---------- SLA classes (U6, CO-0179) ----------

export const TICKET_PRIORITY_KEYS = ['urgent', 'high', 'normal', 'low'] as const;
const Target = z.object({
  firstResponseMinutes: z.number().int().min(1).max(100_000).describe('Office minutes to the first public reply'),
  resolutionMinutes: z.number().int().min(1).max(1_000_000).describe('Office minutes to solved'),
});
const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'A time such as 08:30');
export const SlaTargets = z.object({ urgent: Target, high: Target, normal: Target, low: Target }).openapi('SlaTargets');

export const SlaClass = z
  .object({
    id: Id,
    name: z.string(),
    targets: SlaTargets,
    workdays: z.array(z.number().int().min(1).max(7)).describe('ISO weekdays: 1 is Monday'),
    dayStart: TimeOfDay,
    dayEnd: TimeOfDay,
    holidays: z.enum(['nl', 'none']).describe('nl: the Dutch public holidays are closed'),
    isDefault: z.boolean().describe('For tickets without an engagement or an engagement without a class'),
    engagementCount: z.number().int(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('SlaClass');

const slaClassFields = {
  name: z.string().trim().min(1).max(100),
  targets: SlaTargets,
  workdays: z.array(z.number().int().min(1).max(7)).min(1).max(7).default([1, 2, 3, 4, 5]),
  dayStart: TimeOfDay.default('08:30'),
  dayEnd: TimeOfDay.default('17:30'),
  holidays: z.enum(['nl', 'none']).default('nl'),
  isDefault: z.boolean().default(false),
};

export const listSlaClasses = defineAction({
  name: 'engagement.slaClass.list',
  method: 'get',
  path: '/engagement/sla-classes',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(SlaClass) }),
  summary: 'The SLA classes: response and resolution targets per priority in office minutes, with office hours (CO-0179)',
});

export const createSlaClass = defineAction({
  name: 'engagement.slaClass.create',
  method: 'post',
  path: '/engagement/sla-classes',
  permission: P.keys.update,
  input: z.object(slaClassFields).strict(),
  output: SlaClass,
  summary: 'Add an SLA class; isDefault makes it the class for tickets without one',
});

export const updateSlaClass = defineAction({
  name: 'engagement.slaClass.update',
  method: 'patch',
  path: '/engagement/sla-classes/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id, ...updateFields(slaClassFields), expectedVersion: z.number().int().positive().optional() }).strict(),
  output: SlaClass,
  etag: true,
  summary: 'Change an SLA class; new tickets and changed priorities count with it',
});

export const deleteSlaClass = defineAction({
  name: 'engagement.slaClass.delete',
  method: 'delete',
  path: '/engagement/sla-classes/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: z.object({ id: Id }),
  successStatus: 200,
  summary: 'Delete an SLA class that no engagement uses',
});

// ---------- events ----------

export const engagementChanged = defineEvent({
  type: 'engagement.engagement.changed',
  version: 1,
  payload: z.object({ engagementId: Id, organisationId: Id, change: z.enum(['created', 'updated', 'archived', 'restored']) }),
});

export const rateChanged = defineEvent({
  type: 'engagement.rate.changed',
  version: 1,
  payload: z.object({ rateId: Id, level: RateLevel, change: z.enum(['created', 'updated', 'deleted']) }),
});

// ---------- agreements: the contract register (CO-0173 .. CO-0177, CO-0491) ----------

export const AGREEMENT_KINDS = ['proposal_acceptance', 'framework', 'dpa', 'sla', 'nda', 'other'] as const;
export const AGREEMENT_STATUSES = ['draft', 'sent', 'signed', 'ended'] as const;
export const PARTY_ROLES = ['client', 'supplier'] as const;
export const SIGNED_VIA = ['wet', 'esign', 'email'] as const;
export const AgreementKind = z.enum(AGREEMENT_KINDS);
export const AgreementStatus = z.enum(AGREEMENT_STATUSES);

const AgreementRef = z.object({ id: Id, title: z.string(), kind: AgreementKind, status: AgreementStatus, endsOn: IsoDate.nullable() });

export const Agreement = z
  .object({
    id: Id,
    organisationId: Id,
    organisationName: z.string(),
    engagementId: Id.nullable(),
    engagementName: z.string().nullable(),
    proposalId: Id.nullable().describe('The proposal this agreement accepts, for proposal_acceptance'),
    kind: AgreementKind.describe('proposal_acceptance, framework, dpa (data processing agreement), sla, nda or other'),
    partyRole: z.enum(PARTY_ROLES).describe('client: MKC delivers; supplier: MKC buys'),
    title: z.string(),
    reference: z.string().nullable().describe('The reference of the other party'),
    status: AgreementStatus.describe('draft, sent, signed or ended'),
    ownerId: Id,
    ownerName: z.string().nullable(),
    startsOn: IsoDate.nullable(),
    endsOn: IsoDate.nullable(),
    noticeOn: IsoDate.nullable().describe('The last day to give notice'),
    renewalOn: IsoDate.nullable().describe('The next renewal'),
    autoRenew: z.boolean().describe('Renews tacitly by renewalMonths unless notice is given'),
    renewalMonths: z.number().int().nullable(),
    signedOn: IsoDate.nullable(),
    signedVia: z.enum(SIGNED_VIA).nullable().describe('wet (on paper), esign (an e-signing service) or email (agreed by e-mail, #183)'),
    documentUrl: z.string().nullable().describe('The signed document in the SharePoint folder of MKC (CO-0491)'),
    endedOn: IsoDate.nullable(),
    parentId: Id.nullable().describe('The framework agreement this one falls under (CO-0176)'),
    parentTitle: z.string().nullable(),
    notes: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Agreement');

const Url = z.url().max(2000);
const agreementFields = {
  title: z.string().trim().min(1).max(300),
  kind: AgreementKind,
  partyRole: z.enum(PARTY_ROLES).optional().describe('Default client'),
  reference: z.string().trim().max(200).nullable().optional(),
  engagementId: Id.nullable().optional(),
  proposalId: Id.nullable().optional(),
  ownerId: Id.optional().describe('Default: the caller'),
  startsOn: IsoDate.nullable().optional(),
  endsOn: IsoDate.nullable().optional(),
  noticeOn: IsoDate.nullable().optional(),
  renewalOn: IsoDate.nullable().optional(),
  autoRenew: z.boolean().optional(),
  renewalMonths: z.number().int().min(1).max(120).nullable().optional(),
  documentUrl: Url.nullable().optional(),
  parentId: Id.nullable().optional(),
  notes: z.string().max(20_000).optional(),
};

export const listAgreements = defineAction({
  name: 'engagement.agreement.list',
  method: 'get',
  path: '/engagement/agreements',
  permission: P.keys.read,
  input: ListQuery.extend({
    q: z.string().max(200).optional().describe('Search in title and reference'),
    organisationId: Id.optional(),
    engagementId: Id.optional(),
    kind: CsvEnum(AGREEMENT_KINDS).optional(),
    status: CsvEnum(AGREEMENT_STATUSES).optional(),
    partyRole: z.enum(PARTY_ROLES).optional(),
    endsBefore: IsoDate.optional().describe('Ends on or before this date'),
    noticeBefore: IsoDate.optional().describe('The notice date is on or before this date'),
  }),
  output: Paginated(Agreement),
  summary: 'The contract register: agreements by kind, organisation, status and end date, the first to end first (CO-0177)',
});

export const getAgreement = defineAction({
  name: 'engagement.agreement.get',
  method: 'get',
  path: '/engagement/agreements/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Agreement.extend({ children: z.array(AgreementRef).describe('Agreements that fall under this one (CO-0176)') }),
  etag: true,
});

export const createAgreement = defineAction({
  name: 'engagement.agreement.create',
  method: 'post',
  path: '/engagement/agreements',
  permission: P.keys.create,
  input: z.object({ organisationId: Id, ...agreementFields }).strict(),
  output: Agreement.extend({ existing: z.array(AgreementRef).describe('The other agreements with this organisation (CO-0177)') }),
  summary: 'Add an agreement to the contract register of an organisation, as draft (CO-0173)',
  description: 'Also a paper contract of the client. The answer names the other agreements with the same organisation, so a duplicate shows.',
});

export const updateAgreement = defineAction({
  name: 'engagement.agreement.update',
  method: 'patch',
  path: '/engagement/agreements/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      ...updateFields(agreementFields),
      expectedVersion: ExpectedVersion,
    } as { id: typeof Id; expectedVersion: typeof ExpectedVersion } & { [K in keyof typeof agreementFields]: z.ZodOptional<(typeof agreementFields)[K]> })
    .strict(),
  output: Agreement,
  etag: true,
  summary: 'Change the details, key dates, parent or document link of an agreement; the status changes with setStatus',
});

export const setAgreementStatus = defineAction({
  name: 'engagement.agreement.setStatus',
  method: 'post',
  path: '/engagement/agreements/{id}/status',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      status: z.enum(['sent', 'signed', 'ended']),
      signedOn: IsoDate.optional().describe('signed: when'),
      signedVia: z.enum(SIGNED_VIA).optional().describe('signed: wet, esign or email'),
      documentUrl: Url.optional().describe('signed: the link to the signed document'),
      endedOn: IsoDate.optional().describe('ended: when; default today'),
    })
    .strict(),
  output: Agreement,
  successStatus: 200,
  summary: 'Record that an agreement was sent, signed (date, how, link to the signed document) or ended (CO-0174, CO-0491)',
});

export const deleteAgreement = defineAction({
  name: 'engagement.agreement.delete',
  method: 'delete',
  path: '/engagement/agreements/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }),
  output: z.object({ ok: z.literal(true) }),
  etag: true,
  kind: 'destructive',
  summary: 'Delete an agreement added by mistake or twice; a signed one is ended instead',
  description: 'Fails with agreement-signed (end it) or agreement-has-children (move those first).',
});

export const agreementChanged = defineEvent({
  type: 'engagement.agreement.changed',
  version: 1,
  payload: z.object({
    agreementId: Id,
    organisationId: Id,
    kind: AgreementKind,
    change: z.enum(['created', 'updated', 'status', 'deleted']),
    from: AgreementStatus.nullable().default(null),
    to: AgreementStatus.nullable().default(null),
  }),
});

export const engagementActions = [
  listEngagements,
  getEngagement,
  createEngagement,
  updateEngagement,
  linkLeadToEngagement,
  archiveEngagement,
  restoreEngagement,
  listRates,
  createRate,
  updateRate,
  deleteRate,
  resolveRate,
  getWorkSettings,
  updateWorkSettings,
  listAgreements,
  getAgreement,
  createAgreement,
  updateAgreement,
  setAgreementStatus,
  deleteAgreement,
  listSlaClasses,
  createSlaClass,
  updateSlaClass,
  deleteSlaClass,
];
// ---------------------------------------------------------------- closeout (U9, CO-1321)

export const CLOSEOUT_GOALS = ['yes', 'partly', 'no'] as const;
export const CLOSEOUT_STATUSES = ['draft', 'pending', 'approved'] as const;

/** The engagement got the status closed: a routine drafts the closeout report (CO-1321). */
export const engagementClosed = defineEvent({
  type: 'engagement.engagement.closed',
  version: 1,
  payload: z.object({ engagementId: Id, organisationId: Id, name: z.string() }),
});

/** The closeout report was approved: knowledge keeps the lessons in the space of the client (CO-1321). */
export const closeoutApproved = defineEvent({
  type: 'engagement.closeout.approved',
  version: 1,
  payload: z.object({ closeoutId: Id, engagementId: Id, organisationId: Id, engagementName: z.string(), lessons: z.string() }),
});

export const Closeout = z
  .object({
    id: Id,
    engagementId: Id,
    organisationId: Id,
    status: z.enum(CLOSEOUT_STATUSES).describe('draft: being written; pending: waits for approval; approved: read-only'),
    goalsMet: z.enum(CLOSEOUT_GOALS),
    summary: z.string(),
    lessons: z.string().describe('Markdown'),
    postCalculation: z.record(z.string(), z.unknown()).describe('A snapshot of report.postCalculation.get when it was written'),
    draftedBy: Id,
    draftedByName: z.string().nullable(),
    approvedBy: Id.nullable(),
    approvedByName: z.string().nullable(),
    approvedAt: IsoDateTime.nullable(),
    approvalId: Id.nullable().describe('The pending or last approval request'),
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Closeout');

export const draftCloseout = defineAction({
  name: 'engagement.closeout.draft',
  method: 'put',
  path: '/engagement/engagements/{id}/closeout',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      goalsMet: z.enum(CLOSEOUT_GOALS).describe('Were the goals of the engagement met'),
      summary: z.string().trim().min(1).max(10_000),
      lessons: z.string().trim().min(1).max(50_000).describe('Markdown: what went well, what to do differently'),
      postCalculation: z.record(z.string(), z.unknown()).describe('The result of report.postCalculation.get, kept as it was'),
    })
    .strict(),
  output: Closeout,
  successStatus: 200,
  summary: 'Write the closeout report of a closed engagement as a draft and ask for its approval (CO-1321)',
  description:
    'Only for an engagement with status closed (engagement-not-closed). Writing again changes the draft; the approval request then follows the new text. ' +
    'An approved report is read-only (closeout-approved). A person with engagements:approve approves it in the inbox; an agent never does.',
});

export const getCloseout = defineAction({
  name: 'engagement.closeout.get',
  method: 'get',
  path: '/engagement/engagements/{id}/closeout',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Closeout,
  summary: 'The closeout report of an engagement with its post-calculation and lessons (CO-1321); 404 when there is none',
});

export const engagementEvents = [engagementChanged, rateChanged, agreementChanged, engagementClosed, closeoutApproved];

// ---------------------------------------------------------------- overview (U9, CO-1324)

export const engagementOverview = defineAction({
  name: 'engagement.overview.get',
  method: 'get',
  path: '/engagement/overview',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({
    statuses: z.array(z.object({ status: z.enum(ENGAGEMENT_STATUSES), count: z.number().int() })),
    models: z.array(z.object({ billingModel: z.enum(BILLING_MODELS), count: z.number().int() })).describe('Of the active engagements'),
    retainers: z.array(z.object({ id: Id, name: z.string(), organisationId: Id })).describe('The active retainers; their use per period is billing.retainer.usage'),
    agreementsEnding: z.number().int().describe('Agreements that end, renew or pass their notice date within a month'),
  }),
  summary: 'Engagements at a glance: per status and billing model, the active retainers and the agreements that end soon (CO-1324)',
});
